"use client"

import { useDeepMerge } from '@larvitu/shared/react/hooks';
import { Box } from '@mui/material';
import { cubicBezier, motion } from '../framer-motion-rsc';

// import type { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import type { BoxProps } from '@mui/material';
import type { MotionProps, TargetAndTransition, Variant } from 'framer-motion';
import type { ReactElement, ReactNode } from 'react';

type TargetResolver = Exclude<Variant, TargetAndTransition>;

export type VariantA = Partial<
  Record<'start' | 'end', Omit<TargetAndTransition, 'x'> | TargetResolver>
>;

export type VariantB = Partial<
  Record<'start' | 'end', Omit<TargetAndTransition, 'y'> | TargetResolver>
>;

interface VariantMap {
  up: VariantA;
  down: VariantA;
  left: VariantB;
  right: VariantB;
}

interface FadeInBoxProps<T extends keyof VariantMap>
  extends BoxProps<'div', Omit<MotionProps, 'variants'>>
{
  children: ReactNode;
  type: T;
  variants?: VariantMap[T];
}

const defaultVariants: VariantMap = {
  up: {
    start: {
      y: 75,
      opacity: 0,
    },
    end: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: cubicBezier( 0.47, 0, 0.745, 0.715 ),
      },
    },
  },
  down: {
    start: {
      y: -75,
      opacity: 0,
    },
    end: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: cubicBezier( 0.47, 0, 0.745, 0.715 ),
      },
    },
  },
  left: {
    start: {
      x: 100,
      opacity: 0,
    },
    end: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: cubicBezier( 0.47, 0, 0.745, 0.715 ),
      },
    },
  },
  right: {
    start: {
      x: -100,
      opacity: 0,
    },
    end: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: cubicBezier( 0.47, 0, 0.745, 0.715 ),
      },
    },
  },
};

export const FadeInBox = <T extends keyof VariantMap>(
  { children, type, variants, ...props }: FadeInBoxProps<T>
): ReactElement => {
  const newVariants = useDeepMerge( defaultVariants[type], variants ?? {}, 'overwriteArray' );

  return (
    <Box
      component={motion.div}
      initial="start"
      whileInView="end"
      variants={newVariants}
      {...props}
    >
      {children}
    </Box>
  );
};
